import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { spreadTypes, getSpreadById, SpreadType } from '../data/spreadTypes';
import { allTarotCards, TarotCard } from '../data/tarotCards';
import { allCiganoCards } from '../data/baralhoCiganoCards';
import { allSibillaCards } from '../data/baralhoSibillaCards';
import CardShuffleAnimation from '../components/CardShuffleAnimation';
import CardSelectionGrid from '../components/CardSelectionGrid';
import { consultationService, ConsultationRequest, ConsultationResponse } from '../services/consultationApi';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';

type ConsultationStep = 'question' | 'shuffle' | 'preparation' | 'selection' | 'interpretation';

const DRAFT_KEY = 'consultation_draft';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

interface ConsultationDraft {
  spreadId: string;
  question: string;
  selectedCards: { card: TarotCard; position: number; isReversed: boolean }[];
  savedAt: number;
}

function saveDraft(spreadId: string, question: string, selectedCards: { card: TarotCard; position: number; isReversed: boolean }[]) {
  const draft: ConsultationDraft = { spreadId, question, selectedCards, savedAt: Date.now() };
  localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

function loadDraft(spreadId: string): ConsultationDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft: ConsultationDraft = JSON.parse(raw);
    if (draft.spreadId !== spreadId) return null;
    if (Date.now() - draft.savedAt > DRAFT_TTL_MS) { localStorage.removeItem(DRAFT_KEY); return null; }
    return draft;
  } catch { return null; }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

const renderJustified = (text: string, cls = "text-gray-200 text-sm leading-relaxed text-justify") =>
  text.split('\n').filter(l => l.trim()).map((line, i) => <p key={i} className={cls}>{line}</p>);

// A Responses API (file_search) às vezes vaza marcador de citação inline com colchetes
// full-width 【...】/《...》 no texto — nunca é conteúdo legítimo em português. Sem isso, uma
// leitura antiga onde essa citação virou o "último parágrafo" (o que o backend usa como
// Conselho Prático quando o baralho não usa cabeçalho de conselho) mostra a citação crua
// em vez de um conselho de verdade. Corrigido na origem para leituras novas; isto conserta
// as já salvas.
const stripLeakedCitation = (text?: string | null) => {
  const cleaned = (text || '').replace(/[【《][^】》]{0,400}[】》]/g, '').trim();
  // Se sobrar só pontuação depois de tirar a citação (o conselho era só a citação vazada),
  // trata como vazio de verdade — não mostra a seção com um "." solto.
  return cleaned.replace(/[\s.,;:!?"'-]/g, '').length < 10 ? '' : cleaned;
};

// Helper para dividir "general_interpretation" em "Síntese da Tiragem" e "Interpretação Final"
const parseGeneralInterpretation = (text?: string | null) => {
  if (!text) return { synthesis: '', final: '' };
  const normalized = text.replace(/\r\n/g, '\n');
  // Suporta títulos com ou sem emojis (🔶, 🧭). O ":" depois de "Interpretação Final"
  // é OPCIONAL na fronteira — a Aurora nem sempre repete a pergunta entre aspas no
  // cabeçalho; exigir o ":" fazia esse regex não achar a fronteira, e a Síntese
  // engolia a Interpretação Final inteira (que reaparecia duplicada mais abaixo,
  // já que o finalRegex abaixo sempre encontrava o cabeçalho corretamente).
  const synthRegex = /(?:^|\n)\s*(?:#{1,6}\s*|>\s?)?[*_]{0,2}(?:🔶\s*)?Síntese da Tiragem[*_]{0,2}\s*:?\s*\n([\s\S]*?)(?=\n\s*(?:#{1,6}\s*|>\s?)?[*_]{0,2}(?:🧭\s*)?Interpretação Final[*_]{0,2}\s*:?\s*(?:"[^"]*")?\s*(?:\n|$)|$)/i;
  const finalRegex = /(?:#{1,6}\s*|>\s?)?[*_]{0,2}(?:🧭\s*)?Interpretação Final[*_]{0,2}\s*:?\s*(?:\"[^\"]*\"|)\s*\n?([\s\S]*)$/i;
  const synthMatch = normalized.match(synthRegex);
  const finalMatch = normalized.match(finalRegex);
  const synthesis = synthMatch?.[1]?.trim() || '';
  let final = finalMatch?.[1]?.trim() || (synthesis ? '' : normalized.trim());
  // A Aurora às vezes reemite o cabeçalho "Interpretação Final" no meio do bloco
  // (padrão comum de LLM). Remove o cabeçalho repetido, mantendo a prosa que o segue,
  // pra não renderizar o título duas vezes (uma como <h4>, outra como texto literal).
  final = final
    .replace(/(^|\n)[ \t]*(?:#{1,6}[ \t]*|>[ \t]?)?[*_]{0,2}(?:🧭[ \t]*)?Interpreta[çc][ãa]o Final[*_]{0,2}[ \t]*:?[ \t]*(?:"[^"]*")?[ \t]*/gi, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { synthesis, final };
};

const ConsultationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refresh } = useAuth();
  
  const [currentStep, setCurrentStep] = useState<ConsultationStep>('question');
  const [spreadType, setSpreadType] = useState<SpreadType | null>(null);
  const [question, setQuestion] = useState('');
  const [selectedCards, setSelectedCards] = useState<{ card: TarotCard; position: number; isReversed: boolean }[]>([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [interpretation, setInterpretation] = useState('');
  const [consultationResponse, setConsultationResponse] = useState<ConsultationResponse | null>(null);
  const [isGeneratingInterpretation, setIsGeneratingInterpretation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDraft, setPendingDraft] = useState<ConsultationDraft | null>(null);
  const [deeperReading, setDeeperReading] = useState<string | null>(null);
  const [isDeepening, setIsDeepening] = useState(false);
  const [deepenError, setDeepenError] = useState<string | null>(null);
  const [showAskBox, setShowAskBox] = useState(false);
  const [askQuestion, setAskQuestion] = useState('');
  const [askQA, setAskQA] = useState<{ question: string; answer: string }[]>([]);
  const [isAsking, setIsAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const MAX_ASK_CHARS = 2000;
  const MAX_QUESTION_CHARS = 6000;

  useEffect(() => {
    const spreadId = searchParams.get('spread');
    if (spreadId) {
      const spread = getSpreadById(spreadId);
      if (spread) {
        setSpreadType(spread);
        const draft = loadDraft(spreadId);
        if (draft) {
          setPendingDraft(draft);
        } else {
          // Pergunta ou contexto vindos de uma skill page — pula direto pro embaralhamento
          const raw = searchParams.get('question') || searchParams.get('context') || '';
          const prefilledQuestion = raw ? decodeURIComponent(raw) : '';
          if (prefilledQuestion.trim()) {
            setQuestion(prefilledQuestion);
            saveDraft(spreadId, prefilledQuestion, []);
            setCurrentStep('shuffle');
            setIsShuffling(true);
          }
        }
      }
    }
  }, [searchParams]);

  const handleQuestionSubmit = () => {
    if (question.trim() && question.length <= MAX_QUESTION_CHARS) {
      saveDraft(spreadType!.id, question, []);
      setCurrentStep('shuffle');
      setIsShuffling(true);
    }
  };

  const handleShuffleComplete = () => {
    setIsShuffling(false);
    setCurrentStep('preparation');
  };

  const handlePreparationComplete = () => {
    setCurrentStep('selection');
  };

  const handleCardSelect = (card: TarotCard, position: number) => {
    // Baralho Cigano e Baralho Sibila tradicionalmente não usam cartas invertidas.
    const isReversed = (spreadType?.deck && spreadType.deck !== 'tarot') ? false : Math.random() < 0.3;
    const newCard = { card, position, isReversed };
    setSelectedCards(prev => {
      const updated = [...prev, newCard];
      saveDraft(spreadType!.id, question, updated);
      return updated;
    });
  };

  const handleRestoreDraft = () => {
    if (!pendingDraft) return;
    setQuestion(pendingDraft.question);
    setSelectedCards(pendingDraft.selectedCards);
    setCurrentStep('selection');
    setPendingDraft(null);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setPendingDraft(null);
  };

  const generateInterpretation = async () => {
    setIsGeneratingInterpretation(true);
    setError(null);

    // Helper de mapeamento de ID do frontend para o formato textual do backend
    const suitMap: Record<string, string> = { cups: 'copas', wands: 'paus', swords: 'espadas', pentacles: 'ouros' };
    const getBackendCardId = (card: TarotCard): string => {
      if (card.arcana === 'major') return String(card.id);
      const suit = suitMap[card.suit!];
      const num = card.number!;
      const rankMap: Record<number, string> = { 1: 'as', 11: 'valete', 12: 'cavaleiro', 13: 'rainha', 14: 'rei' };
      const rank = rankMap[num] || String(num);
      return `${suit}_${rank}`;
    };

    // Mapear IDs de tiragem do frontend para os IDs esperados pelo backend
    const backendSpreadMap: Record<string, string> = {
      'tres-cartas': 'tres-cartas-tempo',
      'tres-cartas-decisao': 'tres-cartas-situacao',
      'cruz-celta': 'cruz-celta',
      'metodo-peladan': 'metodo-peladan',
      'templo-afrodite': 'templo-afrodite',
      'sim-nao': 'sim-nao',
      'tarot-dia': 'tarot-dia'
    };
    
    try {
      console.log('Iniciando geração de interpretação...');
      console.log('Usuário:', user);
      console.log('Token:', localStorage.getItem('token'));
      
      // Preparar dados para a API com IDs compatíveis
      const preparedCards = selectedCards.map(({ card, position, isReversed }) => {
        const positionName = spreadType!.positions[position].name;
        return { card, positionName, isReversed };
      });

      const backendSpreadId = backendSpreadMap[spreadType!.id] || spreadType!.id;

      const consultationRequest: ConsultationRequest = {
        question: question,
        spread_type: backendSpreadId,
        selected_cards: preparedCards.map((p) => ({
          position: p.positionName,
          card_id: getBackendCardId(p.card),
          is_reversed: p.isReversed
        }))
      };

      console.log('Dados da consulta:', consultationRequest);

      // Chamar a API certa pro deck (cada baralho tem endpoint próprio — evita
      // colidir IDs de carta entre os catálogos no backend)
      const createByDeck: Record<string, (r: ConsultationRequest) => Promise<ConsultationResponse>> = {
        cigano: consultationService.createCiganoConsultation,
        sibilla: consultationService.createSibillaConsultation,
      };
      const createFn = (spreadType!.deck && createByDeck[spreadType!.deck]) || consultationService.createConsultation;
      const response = await createFn(consultationRequest);
      console.log('Resposta da API:', response);
      
      setConsultationResponse(response);
      setInterpretation(response.general_interpretation);
      clearDraft();
      setCurrentStep('interpretation');
      try { await refresh(); } catch {}
    } catch (error: any) {
      console.error('Erro ao gerar interpretação:', error);
      console.error('Detalhes do erro:', error.response?.data);
      const detail = error?.response?.data?.detail || error.message || 'Erro ao gerar interpretação';
      const friendlyError = detail === 'aurora_unavailable'
        ? '✨ Os astros estão agitados neste momento. Seus tokens não foram descontados. Tente novamente em instantes.'
        : detail;
      setError(friendlyError);
      // Não utilizar fallback genérico: manter etapa atual para o usuário tentar novamente
      return;
    } finally {
      setIsGeneratingInterpretation(false);
    }
  };

  const handleNewConsultation = () => {
    navigate('/dashboard');
  };

  const handleDeepenReading = async () => {
    if (!consultationResponse?.id) return;
    setIsDeepening(true);
    setDeepenError(null);
    try {
      const result = await consultationService.deepenConsultation(consultationResponse.id);
      setDeeperReading(result.deeper_reading);
      try { await refresh(); } catch {}
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || '';
      if (detail === 'aurora_unavailable') {
        setDeepenError('✨ Os astros estão agitados. Seus tokens não foram descontados. Tente novamente em instantes.');
      } else if (err?.response?.status === 402) {
        setDeepenError('Tokens insuficientes. Adquira tokens para aprofundar a leitura.');
      } else {
        setDeepenError('Não foi possível aprofundar a leitura agora. Tente novamente.');
      }
    } finally {
      setIsDeepening(false);
    }
  };

  const handleAskAboutReading = async () => {
    if (!consultationResponse?.id) return;
    const question = askQuestion.trim();
    if (!question) return;
    setIsAsking(true);
    setAskError(null);
    try {
      const result = await consultationService.askConsultation(consultationResponse.id, question);
      setAskQA(prev => [...prev, { question, answer: result.answer }]);
      setAskQuestion('');
      try { await refresh(); } catch {}
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || '';
      if (detail === 'aurora_unavailable') {
        setAskError('✨ Os astros estão agitados. Seus tokens não foram descontados. Tente novamente em instantes.');
      } else if (err?.response?.status === 402) {
        setAskError('Tokens insuficientes. Adquira tokens para fazer uma pergunta.');
      } else {
        setAskError('Não foi possível enviar sua pergunta agora. Tente novamente.');
      }
    } finally {
      setIsAsking(false);
    }
  };


  if (!spreadType) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-mystical mb-4">Carregando consulta...</h2>
          <div className="animate-spin w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link
          to="/dashboard"
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="hidden sm:inline">Voltar</span>
        </Link>
        <h1 className="text-sm sm:text-2xl font-mystical font-bold tracking-wide">CONSULTA DE TAROT</h1>
        <div className="w-10 sm:w-20"></div>
      </div>

      <AuroraLoadingOverlay isLoading={isGeneratingInterpretation} message="Os astros estão falando..." />

      {/* Animação de embaralhamento */}
      <CardShuffleAnimation
        isShuffling={isShuffling}
        onShuffleComplete={handleShuffleComplete}
        duration={3000}
      />

      {/* Modal de restauração de rascunho */}
      {pendingDraft && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1a1033] border border-gold-400/40 rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-2xl">
            <div className="text-4xl mb-4">🃏</div>
            <h3 className="text-xl font-mystical font-bold text-gold-300 mb-2">Tiragem salva encontrada</h3>
            <p className="text-gray-300 mb-2">Você tem uma tiragem de <span className="text-white font-semibold">{spreadType?.name}</span> em andamento.</p>
            <p className="text-gray-400 text-sm mb-6 italic">"{pendingDraft.question.slice(0, 80)}{pendingDraft.question.length > 80 ? '...' : ''}"</p>
            {pendingDraft.selectedCards.length > 0 && (
              <p className="text-gold-400 text-sm mb-6">{pendingDraft.selectedCards.length} carta{pendingDraft.selectedCards.length > 1 ? 's' : ''} já selecionada{pendingDraft.selectedCards.length > 1 ? 's' : ''}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleDiscardDraft}
                className="flex-1 py-3 border border-white/20 text-gray-300 rounded-lg hover:border-white/40 hover:text-white transition-all"
              >
                Começar do zero
              </button>
              <button
                onClick={handleRestoreDraft}
                className="flex-1 py-3 bg-gradient-to-r from-gold-600 to-gold-700 text-white font-semibold rounded-lg hover:from-gold-700 hover:to-gold-800 transition-all"
              >
                Continuar tiragem
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-4 sm:py-8">
        <AnimatePresence mode="wait">
          {/* Etapa 1: Pergunta */}
          {currentStep === 'question' && (
            <motion.div
              key="question"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 sm:p-8 border border-white/20">
                <h2 className="text-xl sm:text-3xl font-mystical font-bold mb-4">
                  Olá, {user?.name || 'Consulente'}!
                </h2>
                <p className="text-gray-300 mb-6">
                  Você escolheu a tiragem <span className="text-gold-300 font-semibold">{spreadType.name}</span>
                </p>
                <p className="text-gray-300 mb-8">
                  {spreadType.description}
                </p>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-left text-sm font-medium mb-2">
                      Faça sua pergunta ao tarot:
                    </label>
                    <textarea
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder="Descreva sua situação com detalhes: o que está vivendo, as emoções envolvidas, o contexto, o que já tentou. Quanto mais você compartilhar, mais precisa e personalizada será a leitura de Aurora."
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-500 resize-none"
                      rows={5}
                      maxLength={MAX_QUESTION_CHARS}
                    />
                    <div className="flex items-start justify-between mt-2 gap-4">
                      <p className="text-xs text-amber-300/80 text-left leading-relaxed">
                        💡 Perguntas ricas em contexto geram respostas muito mais certeiras. Inclua: situação atual, sentimentos, o que deseja entender.
                      </p>
                      <span className="text-xs text-gray-400 flex-shrink-0">{question.length}/{MAX_QUESTION_CHARS}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleQuestionSubmit}
                    disabled={!question.trim() || question.length > MAX_QUESTION_CHARS}
                    className="w-full py-3 bg-gradient-to-r from-gold-600 to-gold-700 text-white font-semibold rounded-lg hover:from-gold-700 hover:to-gold-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Sparkles size={20} />
                    Embaralhar as Cartas
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Etapa 2: Preparação mental */}
          {currentStep === 'preparation' && (
            <motion.div
              key="preparation"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="flex flex-col items-center justify-center min-h-[65vh] text-center px-6"
            >
              {/* Orb pulsante */}
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-br from-purple-700 via-indigo-900 to-purple-900 border border-purple-400/30 shadow-2xl shadow-purple-500/40 mb-10 flex items-center justify-center"
              >
                <span className="text-5xl sm:text-6xl">🔮</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="text-2xl sm:text-4xl font-mystical font-bold text-white mb-3"
              >
                Respire fundo
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.7 }}
                className="text-white/60 text-base sm:text-lg mb-6 max-w-sm leading-relaxed"
              >
                Concentre sua mente na sua pergunta antes de escolher as cartas
              </motion.p>

              {question && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.7 }}
                  className="bg-white/10 border border-white/15 rounded-xl px-6 py-4 text-sm text-white/70 italic max-w-sm mb-10"
                >
                  "{question.length > 140 ? question.slice(0, 140) + '...' : question}"
                </motion.div>
              )}

              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.7 }}
                onClick={handlePreparationComplete}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                className="px-10 py-3 bg-gradient-to-r from-purple-600 to-indigo-700 text-white font-semibold rounded-full shadow-lg shadow-purple-900/50 hover:from-purple-700 hover:to-indigo-800 transition-all duration-300 text-sm sm:text-base"
              >
                Estou pronto — revelar as cartas
              </motion.button>
            </motion.div>
          )}

          {/* Etapa 3: Seleção de cartas */}
          {currentStep === 'selection' && (
            <motion.div
              key="selection"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
            >
              <CardSelectionGrid
                spreadType={spreadType}
                onCardSelect={handleCardSelect}
                selectedCards={selectedCards}
                isSelectionComplete={selectedCards.length === spreadType.positions.length}
                availableCards={
                  spreadType.deck === 'cigano' ? allCiganoCards :
                  spreadType.deck === 'sibilla' ? allSibillaCards :
                  allTarotCards
                }
                onStartReading={generateInterpretation}
                isGenerating={isGeneratingInterpretation}
                error={error}
              />
            </motion.div>
          )}

          {/* Etapa 4: Interpretação */}
          {currentStep === 'interpretation' && (
            <motion.div
              key="interpretation"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-5">
                <h2 className="text-lg sm:text-3xl font-mystical font-bold text-white mb-3">
                  Interpretação da Sua Consulta
                </h2>
                <div className="bg-white/10 rounded-lg p-3 mb-4">
                  <p className="text-gold-300 font-semibold text-sm">Sua pergunta:</p>
                  <p className="text-white italic text-sm">"{question}"</p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/20 mb-6">
                {/* Interpretações individuais das cartas */}
                {consultationResponse && consultationResponse.cards && (
                  <div className="space-y-4">
                    <h3 className="text-base sm:text-xl font-mystical font-bold text-gold-300 text-center">
                      Interpretação Individual das Cartas
                    </h3>
                    <div className="grid gap-3">
                      {consultationResponse.cards.map((cardInterpretation, index) => {
                        const cardImage = cardInterpretation.image_url || selectedCards[index]?.card.image;
                        return (
                        <div key={index} className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <div className="flex gap-3">
                            {cardImage && (
                              <div className="flex-shrink-0">
                                <img
                                  src={cardImage}
                                  alt={cardInterpretation.card_name}
                                  className={`w-14 sm:w-20 object-contain rounded-md border border-gold-400/40 shadow-md shadow-black/40 ${cardInterpretation.is_reversed ? 'rotate-180' : ''}`}
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gold-300 text-sm leading-snug mb-1">
                                {cardInterpretation.position}: {cardInterpretation.card_name}
                                {cardInterpretation.is_reversed && (
                                  <span className="text-red-300 text-xs ml-1">(Invertida)</span>
                                )}
                              </h4>
                              <p className="text-gray-300 text-xs mb-1">
                                <strong>Significado:</strong> {cardInterpretation.meaning}
                              </p>
                              <p className="text-gray-200 text-sm leading-relaxed text-justify">
                                {cardInterpretation.interpretation}
                              </p>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Interpretação completa */}
                {(consultationResponse?.general_interpretation || interpretation) && (() => {
                  const fullText = consultationResponse?.general_interpretation || interpretation;
                  const parsed = parseGeneralInterpretation(fullText);
                  return (
                    <div className="mt-5 p-4 sm:p-6 bg-gradient-to-r from-gold-500/20 to-mystical-500/20 rounded-lg border border-gold-400/30">
                      <h3 className="text-base sm:text-lg font-mystical font-bold text-gold-300 mb-3 text-center">
                        Interpretação Completa
                      </h3>
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gold-300 mb-1">Síntese da Tiragem</h4>
                        <div className="space-y-1">{renderJustified(parsed.synthesis || fullText)}</div>
                      </div>
                      {parsed.synthesis && parsed.final && (
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-gold-300 mb-1">Interpretação Final</h4>
                          <div className="space-y-1">{renderJustified(parsed.final)}</div>
                        </div>
                      )}
                      {stripLeakedCitation(consultationResponse?.advice) && (
                        <div>
                          <h4 className="text-sm font-semibold text-gold-300 mb-1">Conselho Prático</h4>
                          <div className="space-y-1">{renderJustified(stripLeakedCitation(consultationResponse?.advice))}</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Aprofundar leitura / Perguntar sobre esta leitura */}
              <div className="mb-4 p-4 bg-white/5 rounded-xl border border-white/10 text-center">
                {!deeperReading && !showAskBox && (
                  <p className="text-gray-300 text-sm mb-3">
                    Quer que Aurora revele camadas mais profundas desta tiragem, ou prefere perguntar algo específico sobre ela?
                  </p>
                )}
                {deepenError && (
                  <p className="text-amber-300 text-xs mb-3">{deepenError}</p>
                )}
                {askError && (
                  <p className="text-amber-300 text-xs mb-3">{askError}</p>
                )}
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {!deeperReading && (
                    <button
                      onClick={handleDeepenReading}
                      disabled={isDeepening}
                      className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-full font-semibold text-sm hover:from-indigo-700 hover:to-purple-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isDeepening ? (
                        <>
                          <motion.span animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}>✦</motion.span>
                          Aurora está aprofundando…
                        </>
                      ) : (
                        <>✨ Aprofundar leitura <span className="text-white/60 text-xs">(1 token)</span></>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setShowAskBox(v => !v)}
                    className="px-6 py-2.5 bg-white/10 text-white rounded-full font-semibold text-sm hover:bg-white/20 transition-all flex items-center gap-2 border border-white/20"
                  >
                    ❓ Perguntar sobre esta leitura <span className="text-white/60 text-xs">(1 token)</span>
                  </button>
                </div>

                {showAskBox && (
                  <div className="mt-4 text-left max-w-xl mx-auto">
                    <textarea
                      value={askQuestion}
                      onChange={(e) => setAskQuestion(e.target.value.slice(0, MAX_ASK_CHARS))}
                      placeholder="Ex: o que a carta do Presente quer dizer sobre agir agora?"
                      rows={3}
                      className="w-full rounded-lg bg-white/5 border border-white/20 text-white text-sm p-3 focus:outline-none focus:border-gold-400/60 resize-none"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-gray-400">{askQuestion.length}/{MAX_ASK_CHARS}</span>
                      <button
                        onClick={handleAskAboutReading}
                        disabled={isAsking || !askQuestion.trim()}
                        className="px-5 py-2 bg-gradient-to-r from-gold-500 to-amber-600 text-white rounded-full font-semibold text-sm hover:from-gold-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isAsking ? (
                          <>
                            <motion.span animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}>✦</motion.span>
                            Aurora está respondendo…
                          </>
                        ) : (
                          'Enviar pergunta'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {askQA.length > 0 && (
                <div className="mb-4 space-y-3">
                  {askQA.map((qa, i) => (
                    <div key={i} className="p-4 sm:p-6 bg-gradient-to-r from-gold-500/10 to-amber-500/10 rounded-xl border border-gold-400/20">
                      <p className="text-gold-300 text-sm font-semibold mb-2">❓ {qa.question}</p>
                      <div className="space-y-1">{renderJustified(qa.answer)}</div>
                    </div>
                  ))}
                </div>
              )}

              {deeperReading && (
                <div className="mb-4 p-4 sm:p-6 bg-gradient-to-r from-indigo-500/15 to-purple-500/15 rounded-xl border border-indigo-400/30">
                  <h3 className="text-base sm:text-lg font-mystical font-bold text-indigo-300 mb-3 text-center">
                    ✨ Análise Aprofundada
                  </h3>
                  <div className="space-y-1">{renderJustified(deeperReading)}</div>
                </div>
              )}

              <div className="text-center space-y-4">
                <button
                  onClick={handleNewConsultation}
                  className="px-8 py-3 bg-gradient-to-r from-mystical-500 to-mystical-600 text-white rounded-full font-semibold hover:from-mystical-600 hover:to-mystical-700 transition-all transform hover:scale-105"
                >
                  Nova Consulta
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ConsultationPage;